// Liste des contrats de maintenance (option « contrats_maintenance ») : filtres statut / recherche, création d'un brouillon.
// Le technicien n'a pas cet écran ; les champs de prix n'apparaissent qu'avec le droit contrats.prix.
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Library, Plus, Search } from 'lucide-react';
import { LIBELLES_MODE_FACTURATION, LIBELLES_PERIODICITE_FACTURATION, MODES_FACTURATION, PERIODICITES_FACTURATION, STATUTS_CONTRAT, LIBELLES_STATUT_CONTRAT } from '@shared/contrats/contrats.ts';
import { api, messageErreur, ErreurApi } from '../../lib/api.ts';
import { date, euros, pluriel } from '../../lib/format.ts';
import { Badge, Bouton, Chargement, Champ, ChampChoix, Dialogue, EntetePage, EtatErreur, EtatVide, Panneau, useNotifier } from '../../components/ui.tsx';
import { BadgeStatutContrat, GardeContrats, montantSaisi } from './commun.tsx';
import type { ContratListe, DetailContrat, Referentiels } from './types.ts';

export function PageContrats() {
  return <GardeContrats><ListeContrats /></GardeContrats>;
}

function ListeContrats() {
  const [params, setParams] = useSearchParams();
  const statut = params.get('statut') ?? '';
  const [texte, setTexte] = useState(params.get('q') ?? '');
  const [q, setQ] = useState(texte);
  const [creation, setCreation] = useState(false);
  useEffect(() => { const t = setTimeout(() => setQ(texte.trim()), 300); return () => clearTimeout(t); }, [texte]);
  const changer = (k: string, v: string) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); setParams(p, { replace: true }); };
  const refs = useQuery({ queryKey: ['contrats-referentiels'], queryFn: () => api.get<Referentiels>('/api/contrats/referentiels') });
  const url = `/api/contrats?${new URLSearchParams({ ...(statut ? { statut } : {}), ...(q ? { q } : {}) })}`;
  const liste = useQuery({ queryKey: ['contrats', url], queryFn: () => api.get<{ contrats: ContratListe[] }>(url) });
  const droits = refs.data?.droits;
  const filtre = !!(statut || q);

  return (
    <div className="pile-l">
      <EntetePage titre="Contrats de maintenance" sousTitre="Contrats signés avec vos clients professionnels, collectivités et syndicats de copropriété : périmètre, niveaux de service, visites."
        actions={<>
          {droits?.modeles && <Link className="btn" to="/contrats/modeles"><Library aria-hidden />Modèles de contrat</Link>}
          {droits?.ecrire && <Bouton variante="principal" icone={<Plus aria-hidden />} onClick={() => setCreation(true)}>Nouveau contrat</Bouton>}
        </>} />
      <div className="int-filtres cm-filtres" role="search" aria-label="Filtrer les contrats">
        <label className="int-recherche">
          <Search size={18} aria-hidden className="discret" />
          <span className="visuellement-cache">Rechercher un contrat</span>
          <input className="saisie" type="search" placeholder="Référence, intitulé, client" value={texte} onChange={(e) => setTexte(e.target.value)} />
        </label>
        <ChampChoix libelle="Statut" value={statut} onChange={(e) => changer('statut', e.target.value)} vide="Tous les statuts"
          options={STATUTS_CONTRAT.map((s) => ({ valeur: s, libelle: LIBELLES_STATUT_CONTRAT[s] }))} />
      </div>
      {liste.isLoading && <Chargement />}
      {liste.isError && <EtatErreur erreur={liste.error} reessayer={() => liste.refetch()} />}
      {liste.data && (
        <Panneau serre titre={pluriel(liste.data.contrats.length, 'contrat', 'contrats')}>
          {liste.data.contrats.length === 0
            ? <EtatVide icone={<FileText />} titre={filtre ? 'Aucun contrat ne correspond' : 'Aucun contrat de maintenance'}
                texte={filtre ? 'Modifiez les filtres ou la recherche.' : 'Créez un contrat depuis le modèle : client, sites couverts, puis formule, niveaux de service et visites.'}
                action={!filtre && droits?.ecrire ? <Bouton variante="principal" onClick={() => setCreation(true)}>Créer un contrat</Bouton> : undefined} />
            : (
              <div className="liste">
                {liste.data.contrats.map((c) => (
                  <Link key={c.id} to={`/contrats/${c.id}`} className="liste-element">
                    <span className="liste-element-corps">
                      <span className="liste-element-titre">{c.intitule}</span>
                      <span className="petit discret">
                        <span className="mono">{c.reference}</span>{c.version_no > 1 && ` · version ${c.version_no}`} · {c.client_nom} · {pluriel(c.nb_sites, 'site', 'sites')}
                        {c.prochaine_visite && <> · prochaine visite {date(c.prochaine_visite)}</>}
                        {c.prix_annuel_ht != null && <> · {euros(c.prix_annuel_ht)} HT/an</>}
                      </span>
                      <span className="int-ligne-badges">
                        <BadgeStatutContrat statut={c.statut} />
                        {c.visites_en_retard > 0 && <Badge ton="danger">{pluriel(c.visites_en_retard, 'visite en retard', 'visites en retard')}</Badge>}
                        {c.demo && <Badge ton="neutre">Démo</Badge>}
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            )}
        </Panneau>
      )}
      {refs.data && <DialogueCreation refs={refs.data} ouvert={creation} fermer={() => setCreation(false)} />}
    </div>
  );
}

function DialogueCreation({ refs, ouvert, fermer }: { refs: Referentiels; ouvert: boolean; fermer: () => void }) {
  const nav = useNavigate(); const qc = useQueryClient(); const notifier = useNotifier();
  const [intitule, setIntitule] = useState('');
  const [clientId, setClientId] = useState('');
  const [nature, setNature] = useState('');
  const [siteIds, setSiteIds] = useState<string[]>([]);
  const [siren, setSiren] = useState('');
  const [prix, setPrix] = useState('');
  const [periodicite, setPeriodicite] = useState('');
  const [mode, setMode] = useState('');
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const client = refs.clients.find((c) => c.id === clientId);
  // Sites du client choisi (ou sans client rattaché) : un contrat ne couvre que les sites d'un même client.
  const sites = useMemo(() => refs.sites.filter((s) => !clientId || s.client_id === clientId || s.client_id === null), [refs.sites, clientId]);
  useEffect(() => {
    if (!client) return;
    if (client.nature_juridique) setNature(client.nature_juridique);
    if (client.siren) setSiren(client.siren);
    setSiteIds(refs.sites.filter((s) => s.client_id === client.id).map((s) => s.id));
  }, [clientId]);
  useEffect(() => { if (ouvert) setErreurs({}); }, [ouvert]);
  const basculer = (id: string) => setSiteIds((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));
  const pret = intitule.trim().length > 0 && !!clientId && !!nature && siteIds.length > 0;
  const creer = useMutation({
    mutationFn: () => api.post<DetailContrat>('/api/contrats', {
      intitule: intitule.trim(), clientId, nature, siteIds,
      ...(siren.trim() ? { siren: siren.replace(/\s/g, '') } : {}),
      ...(refs.droits.prix && montantSaisi(prix) ? { prixAnnuelHt: montantSaisi(prix) } : {}),
      ...(refs.droits.prix && periodicite ? { periodiciteFacturation: periodicite } : {}),
      ...(refs.droits.prix && mode ? { modeFacturation: mode } : {}),
    }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ['contrats'] }); fermer(); notifier(`Brouillon ${r.contrat.reference} créé.`); nav(`/contrats/${r.contrat.id}`); },
    onError: (e) => {
      if (e instanceof ErreurApi && Array.isArray(e.details)) setErreurs(Object.fromEntries((e.details as { champ: string; message: string }[]).map((x) => [x.champ, x.message])));
      notifier(messageErreur(e), true);
    },
  });
  return (
    <Dialogue titre="Nouveau contrat de maintenance" ouvert={ouvert} fermer={fermer} large
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" disabled={!pret} chargement={creer.isPending} onClick={() => creer.mutate()}>Créer le brouillon</Bouton></>}>
      <div className="pile">
        <Champ libelle="Intitulé" value={intitule} onChange={(e) => setIntitule(e.target.value)} maxLength={200} placeholder="Maintenance GTB du siège" erreur={erreurs.intitule} />
        <div className="grille-form">
          <ChampChoix libelle="Client" value={clientId} onChange={(e) => setClientId(e.target.value)} vide="Choisir le client"
            options={refs.clients.map((c) => ({ valeur: c.id, libelle: c.name }))} erreur={erreurs.clientId} />
          <ChampChoix libelle="Type de client" value={nature} onChange={(e) => setNature(e.target.value)} vide="Choisir le type"
            options={refs.natures.filter((n) => n.disponible).map((n) => ({ valeur: n.code, libelle: n.libelle }))} erreur={erreurs.nature}
            aide="Contrats pour les professionnels, collectivités et syndicats de copropriété." />
          <Champ libelle="SIREN du client" value={siren} onChange={(e) => setSiren(e.target.value)} inputMode="numeric" maxLength={11} mono erreur={erreurs.siren} />
        </div>
        <fieldset className="cm-sites">
          <legend className="petit">Sites couverts</legend>
          {sites.length === 0
            ? <p className="petit discret">Aucun site autorisé pour ce client.</p>
            : sites.map((s) => (
              <label key={s.id} className="case">
                <input type="checkbox" checked={siteIds.includes(s.id)} onChange={() => basculer(s.id)} />
                <span className="petit">{s.name}{s.city ? ` — ${s.city}` : ''}{s.demo ? ' (démo)' : ''}</span>
              </label>
            ))}
          {erreurs.siteIds && <p className="petit texte-danger">{erreurs.siteIds}</p>}
        </fieldset>
        {refs.droits.prix && (
          <div className="grille-form">
            <Champ libelle="Prix annuel HT (€)" value={prix} onChange={(e) => setPrix(e.target.value)} inputMode="decimal" erreur={erreurs.prixAnnuelHt} />
            <ChampChoix libelle="Facturation" value={periodicite} onChange={(e) => setPeriodicite(e.target.value)} vide="À préciser"
              options={PERIODICITES_FACTURATION.map((p) => ({ valeur: p, libelle: LIBELLES_PERIODICITE_FACTURATION[p] }))} />
            <ChampChoix libelle="Échéance" value={mode} onChange={(e) => setMode(e.target.value)} vide="À préciser"
              options={MODES_FACTURATION.map((m) => ({ valeur: m, libelle: LIBELLES_MODE_FACTURATION[m] }))} />
          </div>
        )}
        <p className="tres-petit discret">Le brouillon reprend le modèle de contrat du type de client : clauses obligatoires verrouillées, durée 12 mois, préavis 90 jours. Tout se règle ensuite sur la fiche du contrat.</p>
      </div>
    </Dialogue>
  );
}
